import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { IoArrowBack, IoCheckmark, IoEye, IoEyeOff, IoChevronUp, IoChevronDown, IoTrash, IoCreate, IoAdd } from 'react-icons/io5';
import { useBooks } from '../context/BooksContext';
import { motion, AnimatePresence } from 'framer-motion';
import './WriteChapter.css';


export default function WriteChapter() {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const { getBookById, addChapter, updateBook } = useBooks();
  const book = getBookById(bookId);
  const [form, setForm] = useState({ title: '', content: '' });
  const [saved, setSaved] = useState('');
  const [preview, setPreview] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showEditor, setShowEditor] = useState(true);

  if (!book) {
    return (
      <div className="page" style={{ textAlign: 'center', paddingTop: 60 }}>
        <p style={{ fontSize: 48, marginBottom: 12 }}>📖</p>
        <h2>Livre introuvable</h2>
        <button className="btn btn-primary" onClick={() => navigate('/author')} style={{ marginTop: 16 }}>Retour</button>
      </div>
    );
  }

  const chapters = book.chapters || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.content) return;

    if (editingIndex !== null) {
      // Mode édition
      const updated = [...chapters];
      updated[editingIndex] = { ...updated[editingIndex], title: form.title, content: form.content };
      await updateBook(bookId, { chapters: updated });
      setSaved('Chapitre modifié !');
      setEditingIndex(null);
    } else {
      // Nouveau chapitre
      await addChapter(bookId, form);
      setSaved('Chapitre ajouté !');
    }

    setForm({ title: '', content: '' });
    setTimeout(() => setSaved(''), 2000);
  };

  const handleEditChapter = (index) => {
    const ch = chapters[index];
    setForm({ title: ch.title, content: ch.content });
    setEditingIndex(index);
    setShowEditor(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setForm({ title: '', content: '' });
    setEditingIndex(null);
  };

  const handleDeleteChapter = async (index) => {
    if (!window.confirm('Supprimer ce chapitre ? Cette action est irréversible.')) return;
    const updated = chapters.filter((_, i) => i !== index);
    // Recalculer les IDs
    const reindexed = updated.map((ch, i) => ({ ...ch, id: 'ch' + (i + 1) }));
    await updateBook(bookId, { chapters: reindexed });
    setSaved('Chapitre supprimé');
    if (editingIndex === index) {
      setForm({ title: '', content: '' });
      setEditingIndex(null);
    }
    setTimeout(() => setSaved(''), 2000);
  };

  const handleMoveChapter = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= chapters.length) return;
    const updated = [...chapters];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    // Recalculer les IDs
    const reindexed = updated.map((ch, i) => ({ ...ch, id: 'ch' + (i + 1) }));
    await updateBook(bookId, { chapters: reindexed });
    setSaved('Ordre mis à jour');
    if (editingIndex === index) setEditingIndex(newIndex);
    else if (editingIndex === newIndex) setEditingIndex(index);
    setTimeout(() => setSaved(''), 2000);
  };

  const renderPreview = (text) => {
    if (!text) return '';
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/_(.*?)_/g, '<em>$1</em>')
      .replace(/^## (.*)$/gm, '<h2 style="font-family:var(--font-display);font-size:20px;margin:16px 0 8px">$1</h2>')
      .replace(/^> (.*)$/gm, '<blockquote style="border-left:3px solid var(--amber);padding-left:12px;font-style:italic;opacity:0.7">$1</blockquote>')
      .replace(/^---$/gm, '<hr style="border:none;border-top:1px solid var(--parchment-dark);margin:24px 0" />')
      .replace(/\n/g, '<br />');
  };

  const plainText = form.content.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();
  const wordCount = plainText ? plainText.split(/\s+/).length : 0;

  return (
    <div className="page">
      <button className="btn btn-ghost" onClick={() => navigate('/author')} style={{ marginBottom: 16 }}>
        <IoArrowBack /> Retour
      </button>

      <h1 className="page-title">{book.title}</h1>
      <p className="page-subtitle" style={{ marginBottom: 24 }}>
        {chapters.length} chapitre{chapters.length > 1 ? 's' : ''}
      </p>

      <AnimatePresence>
        {saved && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            style={{
              background: 'var(--sage)', color: 'white', padding: '12px 16px',
              borderRadius: 'var(--radius-md)', marginBottom: 16, display: 'flex',
              alignItems: 'center', gap: 8,
            }}
          >
            <IoCheckmark /> {saved}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== LISTE DES CHAPITRES ===== */}
      {chapters.length > 0 && (
        <div className="chapters-manager">
          <div className="chapters-manager-header">
            <h3>Chapitres existants</h3>
            <button className="btn btn-ghost" style={{ fontSize: 12, padding: '6px 10px' }} onClick={() => { setShowEditor(true); setEditingIndex(null); setForm({ title: '', content: '' }); }}>
              <IoAdd /> Nouveau
            </button>
          </div>

          <div className="chapters-list">
            {chapters.map((ch, i) => (
              <motion.div
                key={ch.id || i}
                className={'chapter-list-item ' + (editingIndex === i ? 'chapter-list-editing' : '')}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.03 }}
              >
                <div className="chapter-list-left">
                  <span className="chapter-list-num">{i + 1}</span>
                  <div className="chapter-list-info">
                    <strong>{ch.title || 'Sans titre'}</strong>
                    <span>{(ch.content || '').split(/\s+/).length} mots</span>
                  </div>
                </div>

                <div className="chapter-list-actions">
                  <button className="chapter-action-btn" onClick={() => handleMoveChapter(i, -1)} disabled={i === 0} title="Monter">
                    <IoChevronUp />
                  </button>
                  <button className="chapter-action-btn" onClick={() => handleMoveChapter(i, 1)} disabled={i === chapters.length - 1} title="Descendre">
                    <IoChevronDown />
                  </button>
                  <button className="chapter-action-btn chapter-action-edit" onClick={() => handleEditChapter(i)} title="Modifier">
                    <IoCreate />
                  </button>
                  <button className="chapter-action-btn chapter-action-delete" onClick={() => handleDeleteChapter(i)} title="Supprimer">
                    <IoTrash />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* ===== ÉDITEUR ===== */}
      {showEditor && (
        <div className="chapter-editor-section">
          <h3 className="chapter-editor-title">
            {editingIndex !== null ? `Modifier — ${chapters[editingIndex]?.title || 'Chapitre'}` : 'Nouveau chapitre'}
          </h3>

          {editingIndex !== null && (
            <button className="btn btn-ghost" style={{ fontSize: 12, marginBottom: 12 }} onClick={handleCancelEdit}>
              Annuler la modification
            </button>
          )}

          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <label>Titre du chapitre</label>
              <input className="input" placeholder="Chapitre 1 — Le commencement" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
            </div>

            <div className="input-group">
              <label>Contenu</label>
              <div className="rich-editor-toolbar">
                <button type="button" className="rich-btn" onClick={() => document.execCommand('bold')} title="Gras"><strong>G</strong></button>
                <button type="button" className="rich-btn" onClick={() => document.execCommand('italic')} title="Italique"><em>I</em></button>
                <button type="button" className="rich-btn" onClick={() => document.execCommand('formatBlock', false, 'h2')} title="Titre">H</button>
                <button type="button" className="rich-btn" onClick={() => document.execCommand('formatBlock', false, 'blockquote')} title="Citation">❝</button>
                <button type="button" className="rich-btn" onClick={() => document.execCommand('insertHorizontalRule')} title="Séparateur">—</button>
                <button type="button" className="rich-btn" onClick={() => document.execCommand('formatBlock', false, 'p')} title="Paragraphe">¶</button>
              </div>
              <div
                className="rich-editor-content"
                contentEditable
                id="chapter-editor"
                onInput={(e) => setForm({ ...form, content: e.currentTarget.innerHTML })}
                dangerouslySetInnerHTML={{ __html: form.content }}
                suppressContentEditableWarning
              />
            </div>

            <div className="word-counter">
              {wordCount} mots · {form.content.length} caractères · ~{Math.ceil(wordCount / 200)} min de lecture
            </div>

            <button
              type="button"
              className="btn btn-ghost btn-full"
              onClick={() => setPreview(!preview)}
              style={{ padding: 12, fontSize: 14, marginBottom: 10, gap: 8 }}
            >
              {preview ? <><IoEyeOff /> Fermer la preview</> : <><IoEye /> Prévisualiser</>}
            </button>

            {preview && form.content && (
              <div className="chapter-preview">
                <h3 className="chapter-preview-title">{form.title || 'Sans titre'}</h3>
                <div className="chapter-preview-content" dangerouslySetInnerHTML={{ __html: renderPreview(form.content) }} />
              </div>
            )}

            <button type="submit" className="btn btn-primary btn-full" style={{ padding: 16, fontSize: 16 }}>
              {editingIndex !== null ? 'Sauvegarder les modifications' : 'Publier le chapitre'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}